import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useEffect, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import Link from "@khanacademy/wonder-blocks-link";
import {
    semanticColor,
    sizing,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    Body,
    LabelSmall,
    LabelXSmall,
} from "@khanacademy/wonder-blocks-typography";
import {useCoeditingState} from "@khan/khanmigo-coediting";
import {RequestInfo} from "@khan/shared-core";

import cloudCheckIcon from "@phosphor-icons/core/regular/cloud-check.svg";
import cloudErrorIcon from "@phosphor-icons/core/regular/cloud-warning.svg";

interface Props {
    viewDocumentsHref: string;
}

function TooltipContent({
    label,
    lastSaved,
}: {
    label: string;
    lastSaved: number | null;
}) {
    return (
        <View style={styles.content}>
            <Body>{label}</Body>
            {lastSaved && (
                <LabelXSmall>
                    {new Date(lastSaved).toLocaleString(RequestInfo.kaLocale, {
                        year: "2-digit",
                        month: "numeric",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                    })}
                </LabelXSmall>
            )}
        </View>
    );
}

export const Autosaver = ({viewDocumentsHref}: Props) => {
    const [lastSaved, setLastSaved] = useState<number | null>(null);
    const {savingStatus} = useCoeditingState();

    useEffect(() => {
        // Preload the error icon, just in case there's a failure.
        const image = new Image();
        image.src = cloudErrorIcon;
    }, []);

    useEffect(() => {
        switch (savingStatus) {
            case "loading":
                return;
            case "saved":
                setLastSaved(Date.now());
                break;
            default:
                setLastSaved(null);
        }
    }, [savingStatus]);

    if (savingStatus === "idle") {
        return null;
    }

    const hasError = savingStatus === "error";
    const label = hasError ? t`Failed to autosave` : t`Saved automatically`;
    const icon = hasError ? cloudErrorIcon : cloudCheckIcon;

    return (
        <View style={styles.wrapper}>
            <Tooltip
                content={<TooltipContent label={label} lastSaved={lastSaved} />}
            >
                <IconButton
                    testId="autosave-indicator"
                    icon={icon}
                    kind="tertiary"
                    href={viewDocumentsHref}
                    aria-label={label}
                    actionType={hasError ? "destructive" : "neutral"}
                    style={styles.icon}
                />
            </Tooltip>

            {hasError ? (
                <View style={styles.errorLabel}>{label}</View>
            ) : lastSaved ? (
                <LabelSmall>
                    {t`Saved to`}{" "}
                    <Link href={viewDocumentsHref} testId="my-documents-link">
                        {t`My Documents`}
                    </Link>
                </LabelSmall>
            ) : null}
        </View>
    );
};

const styles = StyleSheet.create({
    wrapper: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    },
    content: {
        padding: `${spacing.small_12}px ${spacing.medium_16}px`,
    },
    errorLabel: {
        color: semanticColor.status.critical.foreground,
    },
    icon: {
        margin: sizing.size_0,
        marginInlineEnd: sizing.size_040,
        flexShrink: 0,
    },
});
